import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAuthToken } from "../../../utils/getAuthToken";

// Maps the sidebar's status filter keys onto the backend campaign status enum.
export const BROADCAST_STATUS_TO_BACKEND = {
  all: undefined,
  draft: "PENDING",
  scheduled: "SCHEDULED",
  in_progress: "IN_PROGRESS",
  completed: "COMPLETED",
  failed: "FAILED",
};

// Async thunk to fetch broadcasts (campaigns) across the whole account
export const fetchBroadcasts = createAsyncThunk(
  'broadcasts/fetchBroadcasts',
  async ({ status, page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found. Please log in again.");
      }

      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (status) params.append('status', status);

      const response = await axios.get(
        `${apiUrl}/api/bulk-messages/campaigns?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return {
        campaigns: response.data.data,
        pagination: response.data.pagination,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch broadcasts'
      );
    }
  }
);

// Async thunk to resend a broadcast's failed messages
export const resendBroadcast = createAsyncThunk(
  'broadcasts/resendBroadcast',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found. Please log in again.");
      }

      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';

      const response = await axios.post(
        `${apiUrl}/api/bulk-messages/campaigns/${campaignId}/resend`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to resend broadcast'
      );
    }
  }
);

// Async thunk to "send again" a completed broadcast — creates a new draft
// campaign (same dataset/template, name stamped with today's date) rather
// than resending the original in place.
export const resendCompletedBroadcast = createAsyncThunk(
  'broadcasts/resendCompletedBroadcast',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found. Please log in again.");
      }

      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';

      const response = await axios.post(
        `${apiUrl}/api/bulk-messages/campaigns/${campaignId}/resend-completed`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create new broadcast'
      );
    }
  }
);

// Async thunk to cancel a broadcast
export const cancelBroadcast = createAsyncThunk(
  'broadcasts/cancelBroadcast',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found. Please log in again.");
      }

      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';

      const response = await axios.post(
        `${apiUrl}/api/bulk-messages/campaigns/${campaignId}/cancel`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return { campaignId, ...response.data.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to cancel broadcast'
      );
    }
  }
);

// Async thunk to permanently delete a broadcast
export const deleteBroadcast = createAsyncThunk(
  'broadcasts/deleteBroadcast',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found. Please log in again.");
      }

      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';

      await axios.delete(
        `${apiUrl}/api/bulk-messages/campaigns/${campaignId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return { campaignId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete broadcast'
      );
    }
  }
);

const initialState = {
  campaigns: [],
  pagination: {
    currentPage: 1,
    totalPages: 0,
    totalCampaigns: 0,
    hasNext: false,
    hasPrev: false,
  },
  loading: false,
  error: null,
};

const broadcastsSlice = createSlice({
  name: 'broadcasts',
  initialState,
  reducers: {
    clearBroadcasts: (state) => {
      state.campaigns = [];
      state.pagination = initialState.pagination;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBroadcasts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBroadcasts.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload.campaigns;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchBroadcasts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resendBroadcast.pending, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.meta.arg.campaignId);
        if (index !== -1) state.campaigns[index].isResending = true;
      })
      .addCase(resendBroadcast.fulfilled, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.payload._id);
        if (index !== -1) {
          state.campaigns[index] = { ...state.campaigns[index], ...action.payload, isResending: false };
        }
      })
      .addCase(resendBroadcast.rejected, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.meta.arg.campaignId);
        if (index !== -1) state.campaigns[index].isResending = false;
        state.error = action.payload;
      })
      .addCase(resendCompletedBroadcast.pending, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.meta.arg.campaignId);
        if (index !== -1) state.campaigns[index].isSendingAgain = true;
      })
      .addCase(resendCompletedBroadcast.fulfilled, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.meta.arg.campaignId);
        if (index !== -1) state.campaigns[index].isSendingAgain = false;
      })
      .addCase(resendCompletedBroadcast.rejected, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.meta.arg.campaignId);
        if (index !== -1) state.campaigns[index].isSendingAgain = false;
        state.error = action.payload;
      })
      .addCase(cancelBroadcast.pending, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.meta.arg.campaignId);
        if (index !== -1) state.campaigns[index].isCancelling = true;
      })
      .addCase(cancelBroadcast.fulfilled, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.payload.campaignId);
        if (index !== -1) {
          state.campaigns[index] = { ...state.campaigns[index], status: action.payload.status, isCancelling: false };
        }
      })
      .addCase(cancelBroadcast.rejected, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.meta.arg.campaignId);
        if (index !== -1) state.campaigns[index].isCancelling = false;
        state.error = action.payload;
      })
      .addCase(deleteBroadcast.pending, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.meta.arg.campaignId);
        if (index !== -1) state.campaigns[index].isDeleting = true;
      })
      .addCase(deleteBroadcast.fulfilled, (state, action) => {
        state.campaigns = state.campaigns.filter((c) => c._id !== action.payload.campaignId);
        state.pagination.totalCampaigns = Math.max(0, state.pagination.totalCampaigns - 1);
      })
      .addCase(deleteBroadcast.rejected, (state, action) => {
        const index = state.campaigns.findIndex((c) => c._id === action.meta.arg.campaignId);
        if (index !== -1) state.campaigns[index].isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearBroadcasts, clearError } = broadcastsSlice.actions;

export default broadcastsSlice.reducer;
